import { request } from './api.js';

// ============================================================
// Types
// ============================================================

interface StylingKpiDelta {
  value: number;
  previousValue: number;
  delta: number;
  deltaPercent: number | null;
}

interface StylingKpiResponse {
  totalStylings: StylingKpiDelta;
  successRate: StylingKpiDelta;
  avgStylingTimeMs: StylingKpiDelta;
  activeStylistCount: StylingKpiDelta;
  date: string;
  previousDate: string;
}

interface StylingTrendPoint {
  date: string;
  totalStylings: number;
  successRate: number | null;
  avgStylingTimeMs: number | null;
  activeStylistCount: number;
  failedCount: number;
}

interface StylingTrendsResponse {
  data: StylingTrendPoint[];
  startDate: string;
  endDate: string;
}

interface StylingEventBreakdownPoint {
  date: string;
  event: string;
  eventCount: number;
  uniqueAccounts: number;
}

interface StylingEventBreakdownResponse {
  data: StylingEventBreakdownPoint[];
}

interface StylingErrorType {
  errorType: string;
  count: number;
  uniqueAccounts: number;
  uniqueTargets: number;
}

interface StylingErrorsResponse {
  dailyRates: {
    date: string;
    totalAttempts: number;
    failedCount: number;
    errorRate: number;
  }[];
  errorTypes: StylingErrorType[];
  retrySessions: {
    totalRetried: number;
    avgRetryCount: number | null;
  };
  summary: {
    totalFailures: number;
    errorRate: number;
    retriedSessionCount: number;
  };
}

interface StylingTimingStep {
  step: string;
  sessionCount: number;
  avgMs: number | null;
  p50Ms: number | null;
  p90Ms: number | null;
}

interface StylingTimingResponse {
  overallTiming: {
    avgMs: number | null;
    p50Ms: number | null;
    p90Ms: number | null;
  };
  steps: StylingTimingStep[];
  dailyTrend: {
    date: string;
    avgMs: number | null;
    p50Ms: number | null;
    p90Ms: number | null;
  }[];
}

export interface SkuAnalysisItem {
  skuId: number;
  displayCount: number;
  selectCount: number;
  selectionRate: number | null;
  avgPosition: number | null;
  imageUrl: string | null;
}

export interface UnderexposedSku {
  skuId: number;
  displayCount: number;
  selectCount: number;
  selectionRate: number | null;
  avgPosition: number | null;
  weightedAvgRating: number | null;
  weightedAvgDesignRating: number | null;
  avgStock: number | null;
  imageUrl: string | null;
}

export interface FilterPattern {
  categories: string;
  colors: string;
  sizes: string;
  brands: string;
  useCount: number;
  sessionCount: number;
  avgResultCount: number | null;
  lowResultCount: number;
}

interface FilterPatternsResponse {
  patterns: FilterPattern[];
}

interface FilterDimensionUsage {
  dimension: string;
  useCount: number;
  useRate: number | null;
  totalEvents: number;
  avgResultWhenUsed: number | null;
  avgResultWhenNotUsed: number | null;
}

interface FilterDimensionUsageResponse {
  dimensions: FilterDimensionUsage[];
  totalEvents: number;
}

export interface StylistEvent {
  event: string;
  timestamp: string;
  stylingSessionId: string | null;
  targetUserId: number | null;
  elapsedTimeMs: number | null;
  properties: Record<string, unknown> | null;
}

interface StylistTimelineResponse {
  accountId: string;
  events: StylistEvent[];
  totalEvents: number;
  sessionCount: number;
}

// ============================================================
// Client
// ============================================================

/**
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business Styling Console APIクライアント。スタイリストKPI・トレンド・イベント内訳・エラー率・セッションタイミングを取得
 * @graph-connects request [calls] HTTPリクエストラッパー呼び出し
 */
export const stylingApi = {
  getKpi: (date?: string) => {
    const params = date ? `?date=${date}` : '';
    return request<StylingKpiResponse>(`/v1/styling/kpi${params}`);
  },
  getTrends: (startDate: string, endDate: string) => {
    const params = new URLSearchParams({ startDate, endDate });
    return request<StylingTrendsResponse>(`/v1/styling/trends?${params.toString()}`);
  },
  getEventBreakdown: (startDate: string, endDate: string) => {
    const params = new URLSearchParams({ startDate, endDate });
    return request<StylingEventBreakdownResponse>(
      `/v1/styling/event-breakdown?${params.toString()}`,
    );
  },
  getErrors: (startDate: string, endDate: string) => {
    const params = new URLSearchParams({ startDate, endDate });
    return request<StylingErrorsResponse>(`/v1/styling/errors?${params.toString()}`);
  },
  getSessionTiming: (startDate: string, endDate: string) => {
    const params = new URLSearchParams({ startDate, endDate });
    return request<StylingTimingResponse>(`/v1/styling/session-timing?${params.toString()}`);
  },
  getSkuAnalysis: (startDate: string, endDate: string) => {
    const params = new URLSearchParams({ startDate, endDate });
    return request<{ skus: SkuAnalysisItem[] }>(`/v1/styling/sku-analysis?${params.toString()}`);
  },
  getPositionAnalysis: (startDate: string, endDate: string) => {
    const params = new URLSearchParams({ startDate, endDate });
    return request<{
      bands: {
        positionBand: string;
        totalImpressions: number;
        selections: number;
        selectionRate: number | null;
      }[];
    }>(`/v1/styling/position-analysis?${params.toString()}`);
  },
  getUnderexposedSkus: (startDate: string, endDate: string) => {
    const params = new URLSearchParams({ startDate, endDate });
    return request<{ skus: UnderexposedSku[] }>(
      `/v1/styling/underexposed-skus?${params.toString()}`,
    );
  },
  getFilterPatterns: (startDate: string, endDate: string) => {
    const params = new URLSearchParams({ startDate, endDate });
    return request<FilterPatternsResponse>(`/v1/styling/filter-patterns?${params.toString()}`);
  },
  getFilterDimensionUsage: (startDate: string, endDate: string) => {
    const params = new URLSearchParams({ startDate, endDate });
    return request<FilterDimensionUsageResponse>(
      `/v1/styling/filter-dimension-usage?${params.toString()}`,
    );
  },
  getStylistTimeline: (accountId: string, startDate: string, endDate: string) => {
    const params = new URLSearchParams({ accountId, startDate, endDate });
    return request<StylistTimelineResponse>(`/v1/styling/stylist-timeline?${params.toString()}`);
  },
};
